/**
 * Tour — memória de "já viu" e modo preview da equipe.
 *
 * REGRAS (todas sofridas em produção, ver o roteiro da feature):
 *  - marca "visto" AO MOSTRAR, não ao concluir: no celular, abandonar no meio
 *    é o caso comum — marcar só no fim repetiria o tour pra sempre.
 *  - chave por TELA e por USUÁRIO. Re-exibir pra todos após mudança grande =
 *    trocar a string da tela no roteiro (`visao-geral` → `visao-geral-v2`),
 *    nunca apagar storage de usuário.
 *  - falha de leitura conta como JÁ VIU: errar pro lado de não mostrar é
 *    melhor que repetir eternamente.
 *  - a memória que vale é a do SERVIDOR, por usuário (GET/PUT /api/tour/vistos,
 *    desde 23/10/2026): uma vez por usuário, em qualquer aparelho. O
 *    localStorage virou cache — e a chave antiga dele, quando existe, é
 *    enviada ao servidor (quem já viu antes da mudança não vê de novo).
 */
import { tourApi } from "./api";

const chave = (tela, userId) => `tour-${tela}-${userId || "anon"}`;
const CHAVE_PREVIEW = "tour-preview";
const chaveRever = (tela) => `tour-rever-${tela}`;

// Cache em memória do que o servidor disse, por usuário. Uma ida ao servidor
// por carregamento do painel; falha não fica em cache (tenta na próxima visita).
let doServidor = { userId: null, telas: null, pedido: null };

/** Promise de {tela: estado} — ou null quando o servidor não respondeu. */
export function carregarVistos(userId) {
  if (doServidor.userId !== userId) doServidor = { userId, telas: null, pedido: null };
  if (doServidor.telas) return Promise.resolve(doServidor.telas);
  if (!doServidor.pedido) {
    doServidor.pedido = tourApi.vistos()
      .then((r) => {
        doServidor.telas = r.data?.telas || {};
        return doServidor.telas;
      })
      .catch(() => {
        doServidor.pedido = null;
        return null;
      });
  }
  return doServidor.pedido;
}

function gravar(tela, userId, estado) {
  try {
    localStorage.setItem(chave(tela, userId), estado);
  } catch {
    // localStorage bloqueado (aba anônima, cota cheia): o servidor guarda.
  }
  if (doServidor.userId === userId && doServidor.telas) {
    if (doServidor.telas[tela] !== "concluido") doServidor.telas[tela] = estado;
  }
  tourApi.marcar(tela, estado).catch(() => {
    // sem servidor fica só o cache do aparelho; na próxima visita a migração
    // de jaViu() manda de novo.
  });
}

/**
 * `servidor` = resultado de carregarVistos (null = não respondeu ⇒ trata como
 * visto). Viu = servidor tem a tela OU o aparelho tem a chave antiga; nesse
 * segundo caso a chave sobe para o servidor (migração).
 */
export function jaViu(tela, userId, servidor) {
  if (!servidor) return true;
  if (servidor[tela]) return true;
  let local = null;
  try {
    local = localStorage.getItem(chave(tela, userId));
  } catch {
    return true; // storage quebrado ⇒ trata como visto
  }
  if (local !== null) {
    gravar(tela, userId, local === "concluido" ? "concluido" : "visto");
    return true;
  }
  return false;
}

export function marcarVisto(tela, userId) {
  gravar(tela, userId, "visto");
}

export function marcarConcluido(tela, userId) {
  gravar(tela, userId, "concluido");
}

/**
 * "Rever tutoriais" da Ajuda: pede para o tour daquela tela rodar mais UMA vez
 * nesta sessão. Não apaga a marca do servidor — apagar e regravar em seguida
 * dava corrida entre as duas chamadas, e o tutorial podia voltar sozinho.
 */
export function rever(tela) {
  try {
    sessionStorage.setItem(chaveRever(tela), "1");
  } catch {
    // sem sessionStorage o Rever não funciona nesse navegador; nada quebra.
  }
}

/** Consome o pedido de Rever (true uma vez só). */
export function consumirRever(tela) {
  try {
    if (sessionStorage.getItem(chaveRever(tela)) !== "1") return false;
    sessionStorage.removeItem(chaveRever(tela));
    return true;
  } catch {
    return false;
  }
}

export function pediuRever(tela) {
  try {
    return sessionStorage.getItem(chaveRever(tela)) === "1";
  } catch {
    return false;
  }
}

/**
 * Preview da equipe: `?tour=1` liga (ignora papel e "já viu", NÃO marca),
 * `?tour=0` desliga. Vive em sessionStorage porque a navegação SPA perde o
 * query param.
 */
export function previewAtivo() {
  try {
    const q = new URLSearchParams(window.location.search).get("tour");
    if (q === "1") sessionStorage.setItem(CHAVE_PREVIEW, "1");
    if (q === "0") sessionStorage.removeItem(CHAVE_PREVIEW);
    return sessionStorage.getItem(CHAVE_PREVIEW) === "1";
  } catch {
    return false;
  }
}

/**
 * Fechar o tour MATA o preview: limpa a sessão E remove o `?tour=1` da URL —
 * senão a releitura do parâmetro religa o modo. No celular a sessão do
 * navegador sobrevive DIAS; sem isso, quem abrir o link de teste leva o tour
 * em toda visita pra sempre, mesmo concluindo.
 */
export function matarPreview() {
  try {
    sessionStorage.removeItem(CHAVE_PREVIEW);
    const url = new URL(window.location.href);
    if (url.searchParams.has("tour")) {
      url.searchParams.delete("tour");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    // history.replaceState pode falhar em contexto restrito; a URL fica com o
    // ?tour=1, o que no pior caso reabre o preview — não quebra nada.
  }
}

/**
 * Decisão de exibir — função PURA pra ser testável fora do React.
 * Devolve: 'mostrar' | 'preview' | 'nao'.
 */
export function decidir({ preview, papel, papeis, pronto, adiar, visto }) {
  if (!pronto) return "nao";            // tela ainda montando os alvos
  if (adiar) return "nao";              // tarefa crítica em andamento: tenta na próxima visita
  if (preview) return "preview";        // equipe testando: ignora papel e "já viu"
  if (papeis && papeis.length && !papeis.includes(papel)) return "nao";
  if (visto) return "nao";
  return "mostrar";
}
